import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useUserPicture } from "../../api/users";
import { EnvelopeIcon } from "../../components/icons/EnvelopeIcon";
import { PersonIcon } from "../../components/icons/PersonIcon";
import { languageOptions } from "../../components/languageOptions";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Form } from "../../components/ui/Form";
import { Page } from "../../components/ui/Page";
import { Field } from "../../components/ui/Field";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLanguagePreference } from "../../hooks/useLanguagePreference";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import { fallbackLanguage, isSupportedLanguage, type SupportedLanguage } from "../../i18n";
import styles from "./ProfilePage.module.css";

interface ProfileFormValues {
  language: SupportedLanguage;
}

export default function ProfilePage() {
  const { t } = useTranslation();
  useLayoutTitle(t("nav.profile"));
  const user = useCurrentUser();
  const { updateLanguage, isSavingLanguage } = useLanguagePreference();
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    defaultValues: {
      language: isSupportedLanguage(user?.preferredLanguage) ? user.preferredLanguage : fallbackLanguage,
    },
  });

  useEffect(() => {
    reset({
      language: isSupportedLanguage(user?.preferredLanguage) ? user.preferredLanguage : fallbackLanguage,
    });
  }, [reset, user?.preferredLanguage]);

  const selectOptions = languageOptions.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

  const onSubmit = handleSubmit(async ({ language }) => {
    setSaved(false);
    if (!isSupportedLanguage(language)) return;
    await updateLanguage(language);
    void qc.invalidateQueries({ queryKey: ["me"] });
    setSaved(true);
  });

  const { data: managerPicture } = useUserPicture(user.manager?.id);

  const managerInitials = [user.manager?.firstName, user.manager?.lastName]
    .filter((n): n is string => Boolean(n))
    .map((n) => n.charAt(0).toUpperCase())
    .join("");

  return (
    <Page width="narrow">
      <Card>
        <h1 className={styles["pageTitle"]}>{t("profile.title")}</h1>

        <section className={styles["section"]}>
          <h2 className={styles["sectionTitle"]}>{t("profile.personalInfo")}</h2>
          <div className={styles["personalGrid"]}>
            <div className={styles["personalFields"]}>
              <div className={styles["field"]}>
                <span className={styles["fieldLabel"]}>{t("admin.name")}</span>
                <div className={styles["readonlyInput"]}>
                  <PersonIcon />
                  {user?.firstName} {user?.lastName}
                </div>
              </div>
              <div className={styles["field"]}>
                <span className={styles["fieldLabel"]}>{t("admin.email")}</span>
                <div className={styles["readonlyInput"]}>
                  <EnvelopeIcon />
                  {user?.email}
                </div>
              </div>
            </div>
            <div className={styles["personalRight"]}>
              <div className={styles["avatar"]}>
                {managerPicture ? (
                  <img
                    src={managerPicture}
                    alt={`${user.manager?.firstName} ${user.manager?.lastName}`}
                    className={styles["avatarPhoto"]}
                  />
                ) : (
                  managerInitials
                )}
              </div>
              <div className={styles["managerInfo"]}>
                <span className={styles["fieldLabel"]}>{t("profile.manager")}</span>
                <p className={styles["managerName"]}>
                  {user.manager ? `${user.manager.firstName} ${user.manager.lastName}` : t("profile.noManager")}
                </p>
                {user.manager?.email && (
                  <a className={styles["managerEmail"]} href={`mailto:${user.manager.email}`}>
                    {user.manager.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className={styles["section"]}>
          <div className={styles["sectionTitle"]}></div>
          <Form noValidate onSubmit={onSubmit}>
            <Field.Select
              label={t("profile.language")}
              error={errors.language?.message}
              options={selectOptions}
              {...register("language", {
                validate: (value) => value.trim().length > 0 || t("common.required"),
              })}
            />
            {saved && <Form.Feedback type="success">{t("profile.languageSaved")}</Form.Feedback>}
            <Form.Actions>
              <Button type="submit" loading={isSavingLanguage}>
                {t("profile.saveLanguage")}
              </Button>
            </Form.Actions>
          </Form>
        </section>
      </Card>
    </Page>
  );
}
